import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Fade,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
  Button,
  useTheme
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TaxComputationCard from "./TaxComputationCard";
import AddTaxComputationCard from "./AddTaxComputationCard";
import TaxComputationDialog from "./TaxComputationDialog";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import ComponentLoading from "../../App/ComponentLoading";
import { formatDateWithYear } from "../../../utils/hooks/dataHandler";
import {
  buildFinancialYearEndDate,
  getFullYearStartFromEnd
} from "../../../utils/hooks/financialYear";
import { useNavigate } from "react-router-dom";
import { TAX_COMPUTATION } from "../../../utils/data/controlData";
import AddIcon from "@mui/icons-material/Add";

const TaxComputationTab = ({
  company,
  checkAccess,
  currencyList,
  createTaxComputation,
  onUpdateTaxComputation,
  onDeleteTaxComputation,
  getTaxComputationList,
  handleSuccess,
  handleError,
  getExchangeRate
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const initialFormData = {
    financialYearEnd: null,
    financialYearStart: null,
    yearOfAssessment: "",
    isFullYear: true,
    taxComputationType: "common",
    taxPeriodType: "single_ya",
    basisPeriods: [],
    isCustomizedBasisPeriod: false,
    currency: {
      country: "SINGAPORE",
      currency: "Singapore Dollar",
      code: "SGD",
      numericCode: 702
    },
    currencyRate: 1
  };

  const [openDialog, setOpenDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [detailsPL, setDetailsPL] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedComputation, setSelectedComputation] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [yearOfAssessmentFilter, setYearOfAssessmentFilter] = useState("");
  const [taxComputationTypeFilter, setTaxComputationTypeFilter] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("");

  // Add access control checks
  const canCreateTaxComp = checkAccess("create", TAX_COMPUTATION);
  const canEditTaxComp = checkAccess("edit", TAX_COMPUTATION);
  const canDeleteTaxComp = checkAccess("delete", TAX_COMPUTATION);
  const companyId = company?.c_id;

  useEffect(() => {
    let isActive = true;

    const fetchDetailsPLList = async () => {
      if (!companyId) {
        setDetailsPL([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const detailsPL = await getTaxComputationList(companyId, {
          skipMasExchangeRate: true,
        });
        if (isActive) {
          setDetailsPL(Array.isArray(detailsPL) ? detailsPL : []);
        }
      } catch (error) {
        if (isActive) {
          handleError(error);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchDetailsPLList();

    return () => {
      isActive = false;
    };
  }, [companyId, getTaxComputationList, handleError]);

  // Get unique filter options from data
  const filterOptions = useMemo(() => {
    if (!Array.isArray(detailsPL) || detailsPL.length === 0) {
      return { years: [], currencies: [], types: [] };
    }
    const years = [
      ...new Set(detailsPL.map((item) => item.yearOfAssessment))
    ].sort((a, b) => b - a);
    const currencies = [
      ...new Set(detailsPL.map((item) => item.currency))
    ].sort();
    const types = [
      ...new Set(detailsPL.map((item) => item.taxComputationType || "common"))
    ];

    return { years, currencies, types };
  }, [detailsPL]);

  // Filter the tax computations
  const filteredDetailsPL = useMemo(() => {
    if (!Array.isArray(detailsPL) || detailsPL.length === 0) {
      return [];
    }
    return detailsPL.filter((item) => {
      // Search term filter (searches in year of assessment and currency)
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          item.yearOfAssessment
            ?.toString()
            .toLowerCase()
            .includes(searchLower) ||
          item.currency?.toLowerCase().includes(searchLower) ||
          item.taxComputationType?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Year of Assessment filter
      if (
        yearOfAssessmentFilter &&
        item.yearOfAssessment !== parseInt(yearOfAssessmentFilter)
      ) {
        return false;
      }

      // Tax Computation Type filter
      if (
        taxComputationTypeFilter &&
        (item.taxComputationType || "common") !== taxComputationTypeFilter
      ) {
        return false;
      }

      // Currency filter
      if (currencyFilter && item.currency !== currencyFilter) {
        return false;
      }

      return true;
    });
  }, [
    detailsPL,
    searchTerm,
    yearOfAssessmentFilter,
    taxComputationTypeFilter,
    currencyFilter
  ]);

  // Check if any filters are active
  const hasActiveFilters =
    searchTerm ||
    yearOfAssessmentFilter ||
    taxComputationTypeFilter ||
    currencyFilter;

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm("");
    setYearOfAssessmentFilter("");
    setTaxComputationTypeFilter("");
    setCurrencyFilter("");
  };

  const onDetailsPLClick = (details) => {
    if (localStorage.getItem("selectedComponent"))
      localStorage.removeItem("selectedComponent");

    navigate(`/main-doc`, { state: { details: details, company: company } });
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => {
      if (field === "financialYearEnd") {
        const nextYear = value
          ? new Date(value).getFullYear() + 1
          : new Date().getFullYear();
        return {
          ...prev,
          [field]: value,
          ["yearOfAssessment"]: nextYear
        };
      } else if (field === "yearOfAssessment" && /^\d{0,4}$/.test(value)) {
        return { ...prev, [field]: value };
      }

      return { ...prev, [field]: value };
    });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setIsEditMode(false);
    setFormData(initialFormData);
  };

  const handleDialogExited = () => {
    setIsEditMode(false);
    setFormData(initialFormData);
    setFormData({
      financialYearEnd: null,
      financialYearStart: null,
      yearOfAssessment: "",
      isFullYear: true,
      taxComputationType: "common",
      taxPeriodType: "single_ya",
      basisPeriods: [],
      isCustomizedBasisPeriod: false,
      currency: {
        country: "SINGAPORE",
        currency: "Singapore Dollar",
        code: "SGD",
        numericCode: 702
      },
      currencyRate: 1
    });
  };

  const handleEdit = (computation) => {
    setIsEditMode(true);

    setFormData({
      tc_id: computation.tc_id,
      financialYearEnd: new Date(computation.financialYearEnd),
      financialYearStart: new Date(computation.financialYearStart),
      yearOfAssessment: computation.yearOfAssessment,
      isFullYear: computation.isFullYear,
      taxComputationType: computation.taxComputationType || "common",
      taxPeriodType: computation.taxPeriodType || "single_ya",
      basisPeriods: computation.basisPeriods || [],
      isCustomizedBasisPeriod:
        computation.taxPeriodType === "cross_ya" &&
        Boolean(computation.isCustomizedBasisPeriod),
      currency: {
        code: computation.currency,
        numericCode: currencyList.filter((c) => {
          return c.code === computation.currency;
        })[0].numericCode,
        currency: currencyList.filter((c) => {
          return c.code === computation.currency;
        })[0].currency
      },
      currencyRate: computation.currency_rate
    });
    setOpenDialog(true);
  };

  const handleDelete = (computation) => {
    setSelectedComputation(computation);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleteLoading(true);
      await onDeleteTaxComputation(selectedComputation);

      const updatedDetailsPL = await getTaxComputationList(company.c_id, {
        skipMasExchangeRate: true,
      });
      setDetailsPL(updatedDetailsPL);
      setDeleteDialogOpen(false);

      handleSuccess(
        "The Year of Assessment " +
          selectedComputation.yearOfAssessment +
          " has been successfully deleted."
      );

      setSelectedComputation(null);
    } catch (error) {
      handleError(error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedComputation(null);
  };

  const handleSubmit = async () => {
    const selectedYearEnd = new Date(formData.financialYearEnd);
    const endMonth = selectedYearEnd.getMonth();
    const endDay = selectedYearEnd.getDate();

    let fyStart = "";
    let fyEnd = "";

    if (formData.isFullYear) {
      const fyEndDate = buildFinancialYearEndDate(
        formData.yearOfAssessment - 1,
        endMonth,
        endDay
      );
      const fyStartDate = getFullYearStartFromEnd(fyEndDate);

      fyStart = formatDateWithYear(fyStartDate);
      fyEnd = formatDateWithYear(fyEndDate);
    } else {
      fyStart = formatDateWithYear(new Date(formData.financialYearStart));
      fyEnd = formatDateWithYear(new Date(formData.financialYearEnd));
    }

    const customizedBasisPeriods = (() => {
      if (
        formData.taxPeriodType !== "cross_ya" ||
        !formData.isCustomizedBasisPeriod ||
        !Array.isArray(formData.basisPeriods) ||
        formData.basisPeriods.length !== 2
      ) {
        return [];
      }

      const splitEnd = new Date(formData.basisPeriods[0].financialYearEnd);
      const currentStart = new Date(splitEnd);
      currentStart.setDate(currentStart.getDate() + 1);

      return [
        {
          yearOfAssessment: Number(formData.yearOfAssessment) - 1,
          financialYearStart: fyStart,
          financialYearEnd: formatDateWithYear(splitEnd)
        },
        {
          yearOfAssessment: Number(formData.yearOfAssessment),
          financialYearStart: formatDateWithYear(currentStart),
          financialYearEnd: fyEnd
        }
      ];
    })();

    const detailsPLData = {
      tc_id: formData.tc_id || null,
      c_id: company.c_id,
      uen: company.uen,
      companyType: company.companyType,
      financialYearEnd: fyEnd,
      financialYearStart: fyStart,
      yearOfAssessment: formData.yearOfAssessment,
      currencyRate: formData.currencyRate,
      currency: formData.currency.code,
      taxComputationType: formData.taxComputationType,
      taxPeriodType: formData.taxPeriodType || "single_ya",
      basisPeriods: customizedBasisPeriods,
      isCustomizedBasisPeriod:
        formData.taxPeriodType === "cross_ya" &&
        Boolean(formData.isCustomizedBasisPeriod)
    };

    try {
      setSubmitLoading(true);
      if (isEditMode) {
        await onUpdateTaxComputation(detailsPLData);
      } else {
        await createTaxComputation(detailsPLData);
      }
      const updatedDetailsPL = await getTaxComputationList(company.c_id, {
        skipMasExchangeRate: true,
      });
      setDetailsPL(updatedDetailsPL);
      handleCloseDialog();
      handleSuccess(
        isEditMode
          ? "Tax Computation has been successfully updated."
          : "Tax Computation has been successfully created."
      );
    } catch (error) {
      handleError(error);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        minWidth: 0,
        overflow: "hidden"
      }}
    >
      {loading ? (
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <ComponentLoading />
        </Box>
      ) : (
        <>
          {/* Compact filters */}
          {detailsPL && detailsPL.length > 0 && (
            <Box
              sx={{
                p: 2,
                flexShrink: 0,
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                "& .MuiOutlinedInput-root": {
                  height: 32,
                  fontSize: "0.8125rem",
                  borderRadius: 1.5,
                  bgcolor: "background.paper",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused": {
                    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
                  },
                },
                "& .MuiSelect-select": { py: 0.625 },
                "& .MuiInputLabel-root": { fontSize: "0.8125rem" },
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateAreas: {
                    xs: '"search summary" "filters filters"',
                    md: '"search filters summary"',
                  },
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr) auto",
                    md: "minmax(160px, 280px) minmax(0, 1fr) auto",
                  },
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <TextField
                  size='small'
                  placeholder='Search...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  inputProps={{ "aria-label": "Search tax computations" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon sx={{ color: "grey.500", fontSize: 18 }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position='end'>
                        <IconButton
                          size='small'
                          aria-label='Clear search'
                          onClick={() => setSearchTerm("")}
                          edge='end'
                          sx={{ p: 0.25 }}
                        >
                          <ClearIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ gridArea: "search", minWidth: 0 }}
                />

                <Box
                  sx={{
                    gridArea: "filters",
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(3, minmax(0, 1fr))",
                      md: "100px minmax(140px, 180px) 100px",
                    },
                    minWidth: 0,
                    gap: 1,
                  }}
                >
                  <FormControl size='small' sx={{ minWidth: 0 }}>
                    <InputLabel id='tax-computation-ya-label' shrink>
                      YA
                    </InputLabel>
                    <Select
                      labelId='tax-computation-ya-label'
                      value={yearOfAssessmentFilter}
                      label='YA'
                      onChange={(e) =>
                        setYearOfAssessmentFilter(e.target.value)
                      }
                      displayEmpty
                      notched
                    >
                      <MenuItem value=''>All</MenuItem>
                      {filterOptions.years.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size='small' sx={{ minWidth: 0 }}>
                    <InputLabel id='tax-computation-type-label' shrink>
                      Type
                    </InputLabel>
                    <Select
                      labelId='tax-computation-type-label'
                      value={taxComputationTypeFilter}
                      label='Type'
                      onChange={(e) =>
                        setTaxComputationTypeFilter(e.target.value)
                      }
                      displayEmpty
                      notched
                    >
                      <MenuItem value=''>All</MenuItem>
                      {filterOptions.types.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type === "investment"
                            ? "Investment Holding"
                            : "Trading"}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size='small' sx={{ minWidth: 0 }}>
                    <InputLabel id='tax-computation-currency-label' shrink>
                      Currency
                    </InputLabel>
                    <Select
                      labelId='tax-computation-currency-label'
                      value={currencyFilter}
                      label='Currency'
                      onChange={(e) => setCurrencyFilter(e.target.value)}
                      displayEmpty
                      notched
                    >
                      <MenuItem value=''>All</MenuItem>
                      {filterOptions.currencies.map((currency) => (
                        <MenuItem key={currency} value={currency}>
                          {currency}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box
                  sx={{
                    gridArea: "summary",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 0.75,
                  }}
                >
                  {hasActiveFilters && (
                    <Tooltip title='Clear all filters'>
                      <IconButton
                        size='small'
                        aria-label='Clear all filters'
                        onClick={handleClearFilters}
                        sx={{ width: 28, height: 28 }}
                      >
                        <ClearIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Typography
                    variant='caption'
                    sx={{
                      color: "text.secondary",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {hasActiveFilters
                      ? `${filteredDetailsPL.length} of ${detailsPL.length}`
                      : `${detailsPL.length} total`}
                  </Typography>
                </Box>
              </Box>

              {hasActiveFilters && (
                <Box
                  sx={{
                    mt: 0.75,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    flexWrap: "wrap",
                    "& .MuiChip-root": {
                      height: 20,
                      maxWidth: "100%",
                      fontSize: "0.7rem",
                    },
                    "& .MuiChip-deleteIcon": { fontSize: 14 },
                  }}
                >
                  {searchTerm && (
                    <Chip
                      label={searchTerm}
                      size='small'
                      onDelete={() => setSearchTerm("")}
                    />
                  )}
                  {yearOfAssessmentFilter && (
                    <Chip
                      label={`YA ${yearOfAssessmentFilter}`}
                      size='small'
                      onDelete={() => setYearOfAssessmentFilter("")}
                    />
                  )}
                  {taxComputationTypeFilter && (
                    <Chip
                      label={
                        taxComputationTypeFilter === "investment"
                          ? "Investment Holding"
                          : "Trading"
                      }
                      size='small'
                      onDelete={() => setTaxComputationTypeFilter("")}
                    />
                  )}
                  {currencyFilter && (
                    <Chip
                      label={currencyFilter}
                      size='small'
                      onDelete={() => setCurrencyFilter("")}
                    />
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Content Area */}
          <Box
            sx={{
              flexGrow: 1,
              minHeight: 0,
              overflow: "auto",
              px: 2.5,
              py: 2
            }}
          >
            {/* Empty State - When no tax computations exist */}
            {detailsPL && detailsPL.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "60vh",
                  px: 3,
                  textAlign: "center"
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    mb: 3
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2
                    }}
                  >
                    <DescriptionIcon
                      sx={{ fontSize: 40, color: "primary.main" }}
                    />
                  </Box>
                </Box>

                <Typography variant='h5' fontWeight={600} gutterBottom>
                  No Tax Computations Yet
                </Typography>

                <Typography
                  variant='body1'
                  color='text.secondary'
                  sx={{ mb: 3, maxWidth: 500 }}
                >
                  Create your first tax computation to get started. You can
                  manage multiple years and types of computations from this
                  dashboard.
                </Typography>

                {canCreateTaxComp && (
                  <Button
                    variant='contained'
                    size='large'
                    onClick={() => setOpenDialog(true)}
                    startIcon={<AddIcon />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: "1rem",
                      boxShadow: 2,
                      "&:hover": {
                        boxShadow: 4,
                        transform: "translateY(-2px)",
                        transition: "all 0.2s"
                      }
                    }}
                  >
                    Create First Tax Computation
                  </Button>
                )}
              </Box>
            ) : (
              /* Grid Layout - When tax computations exist */
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(auto-fill, minmax(280px, 1fr))",
                    sm: "repeat(auto-fill, minmax(300px, 1fr))",
                    md: "repeat(auto-fill, minmax(320px, 1fr))",
                    lg: "repeat(auto-fill, minmax(340px, 1fr))"
                  },
                  gap: 2,
                  alignItems: "start"
                }}
              >
                {/* Add New Card - Only show if user has create access and there are existing items */}
                {canCreateTaxComp &&
                  detailsPL &&
                  detailsPL.length > 0 &&
                  !hasActiveFilters && (
                    <Fade in timeout={300}>
                      <Box>
                        <AddTaxComputationCard
                          onClick={() => setOpenDialog(true)}
                          loading={submitLoading}
                        />
                      </Box>
                    </Fade>
                  )}

                {/* Existing Tax Computations */}
                {filteredDetailsPL && filteredDetailsPL.length > 0 ? (
                  filteredDetailsPL.map((details, index) => (
                    <Fade
                      key={details.tc_id || index}
                      in
                      timeout={300}
                      style={{ transitionDelay: `${index * 50}ms` }}
                    >
                      <Box>
                        <TaxComputationCard
                          details={details}
                          onClick={() => onDetailsPLClick(details)}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          showActions={canEditTaxComp || canDeleteTaxComp}
                          canEditTaxComp={canEditTaxComp}
                          canDeleteTaxComp={canDeleteTaxComp}
                        />
                      </Box>
                    </Fade>
                  ))
                ) : detailsPL && detailsPL.length > 0 ? (
                  <Box
                    sx={{
                      gridColumn: "1 / -1",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 8,
                      px: 2
                    }}
                  >
                    <Typography
                      variant='body1'
                      sx={{
                        color: "text.secondary",
                        mb: 1,
                        fontWeight: 400
                      }}
                    >
                      No tax computations match your filters
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        color: "text.disabled",
                        fontSize: "0.875rem",
                        mb: 2
                      }}
                    >
                      Try adjusting your search or filter criteria
                    </Typography>
                    {canCreateTaxComp && (
                      <Button
                        variant='outlined'
                        onClick={() => setOpenDialog(true)}
                        sx={{
                          mt: 1,
                          px: 2.5,
                          py: 1,
                          borderRadius: 1.5,
                          textTransform: "none",
                          fontWeight: 500,
                          fontSize: "0.875rem"
                        }}
                      >
                        Create Tax Computation
                      </Button>
                    )}
                  </Box>
                ) : (
                  !canCreateTaxComp && (
                    <Box
                      sx={{
                        gridColumn: "1 / -1",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 8,
                        px: 2
                      }}
                    >
                      <Typography
                        variant='body1'
                        sx={{
                          color: "text.secondary",
                          mb: 1,
                          fontWeight: 400
                        }}
                      >
                        No tax computations available
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{
                          color: "text.disabled",
                          fontSize: "0.875rem"
                        }}
                      >
                        Tax computations will appear here once created
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
            )}
          </Box>

          {/* Only render dialogs if user has appropriate permissions */}
          {canCreateTaxComp && (
            <TaxComputationDialog
              open={openDialog}
              onClose={handleCloseDialog}
              onExited={handleDialogExited}
              isEditMode={isEditMode}
              formData={formData}
              currencyList={currencyList}
              onSubmit={handleSubmit}
              onFormChange={handleFormChange}
              getExchangeRate={getExchangeRate}
              submitLoading={submitLoading}
            />
          )}

          {canDeleteTaxComp && (
            <DeleteConfirmationDialog
              open={deleteDialogOpen}
              onClose={handleDeleteCancel}
              onConfirm={handleDeleteConfirm}
              yearOfAssessment={selectedComputation?.yearOfAssessment}
              deleting={deleteLoading}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default TaxComputationTab;
